/**
 * Responsive Grid Component
 *
 * Renders items in a responsive grid layout using the render prop pattern.
 * Uses CSS Grid with auto-fill for responsive column management.
 *
 * Features:
 * - Auto-fill responsive columns with configurable minimum width
 * - Configurable gap spacing
 * - Render prop pattern for flexible item rendering
 * - Works with drag-and-drop libraries (can be wrapped by DragDropContext)
 * - Full TypeScript generics support for type-safe item rendering
 *
 * @template T - The type of items in the grid
 * @param items - Array of items to render in the grid
 * @param renderItem - Function to render each item (receives item and index)
 * @param minWidth - Minimum column width in px (default: 300)
 * @param gap - Grid gap in Tailwind units (default: 6 = 1.5rem)
 * @param className - Additional CSS classes for the grid container
 *
 * @example Basic usage
 * ```tsx
 * <Grid
 *   items={documents}
 *   renderItem={(doc) => <DocumentCard document={doc} onClick={handleClick} />}
 *   minWidth={300}
 *   gap={6}
 * />
 * ```
 *
 * @example With type-safe rendering
 * ```tsx
 * interface Vendor {
 *   id: string;
 *   name: string;
 * }
 *
 * <Grid<Vendor>
 *   items={vendors}
 *   renderItem={(vendor, index) => (
 *     <VendorCard key={vendor.id} vendor={vendor} index={index} />
 *   )}
 *   minWidth={320}
 *   gap={6}
 * />
 * ```
 *
 * @example With custom className
 * ```tsx
 * <Grid
 *   items={contacts}
 *   renderItem={(contact) => <ContactCard contact={contact} />}
 *   minWidth={280}
 *   gap={4}
 *   className="contact-grid"
 * />
 * ```
 */

import React from 'react';

export interface GridProps<T> extends React.HTMLAttributes<HTMLDivElement> {
  /** Array of items to render */
  items: T[];
  /** Function to render each item (receives item and index) */
  renderItem: (item: T, index: number) => React.ReactNode;
  /** Minimum column width in pixels (default: 300) */
  minWidth?: number;
  /** Grid gap in Tailwind units (default: 6 = 1.5rem) */
  gap?: number;
  /** Additional CSS classes for the grid container */
  className?: string;
}

export const Grid = React.forwardRef(<T,>(
  {
    items,
    renderItem,
    minWidth = 300,
    gap = 6,
    className = '',
    ...rest
  }: GridProps<T>,
  ref: React.Ref<HTMLDivElement>
): React.ReactElement => {
  const gapClass = `gap-${gap}`;
  const gridStyle = {
    display: 'grid',
    gridTemplateColumns: `repeat(auto-fill, minmax(${minWidth}px, 1fr))`,
  };

  return (
    <div
      ref={ref}
      className={`grid ${gapClass} ${className}`.trim()}
      style={gridStyle}
      {...rest}
    >
      {items.map((item, index) => renderItem(item, index))}
    </div>
  );
}) as <T>(props: GridProps<T> & { ref?: React.Ref<HTMLDivElement> }) => React.ReactElement;
